/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useRef } from 'react';
import { components } from 'react-select';
import { useVirtual } from 'react-virtual';
import { getOptionValue } from './usePlanPickerApi/items';
import { usePlanPickerContext } from './PlanPickerContext';

/**
 * Workaround for React Select issue "Dynamically updating the option list rests the focus to the first element"
 *
 * See: https://github.com/JedWatson/react-select/issues/5367
 *
 * @param {import('./PlanPickerContext').PlanPickerContextBag['selectRef']} selectRef
 * @param {import('./usePlanPickerApi/items').PlanPickerItemWithStatus[]} options
 */
function useFixReactSelectFocus(selectRef, options) {
    // HACK: This accesses the select class via a ref
    const select = selectRef.current;
    if (select && select.state.focusedOption) {
        const target = select.state.focusedOption;

        /** @type {import('./usePlanPickerApi/items').PlanPickerItemWithStatus | null} */
        let focusedOption = null;

        if (target.status === 'loaded') {
            const targetValue = getOptionValue(target);
            focusedOption = options.find(o => getOptionValue(o) === targetValue) ?? null;
        } else {
            // The sort order of the options list is stable, so we can look up the correct
            // object by index when we don't know it's ID.
            focusedOption = options[target.ix];
        }

        // HACK: manipulating the state without setState is evil (but needed here as we are mid-transition)
        select.state.focusedOption = focusedOption;
        // HACK: react-select doesn't expect us to handle scrolling, and it doesn't expect items to be
        // virtualized. Those two facts means react-select will try to scroll the focused item into
        // view, even though we're already managing scroll, and when it does, it will miscalculate the
        // scroll offset -- causing the option list to jump to the top.
        //
        // But, we do have to mess with react-select's internals here to opt out of its scroll
        // management.
        select.scrollToFocusedOptionOnUpdate = false;
    }
}

/**
 * Scroll the menu list to the focused option only in cases where that behavior
 * makes sense (ex. for keyboard users using the arrow keys to scroll through
 * the list). Otherwise, leave the scroll uncontrolled.
 * @param {UseScrollManagerProps} props
 */
const useScrollManager = ({ focusedIndex, firstVisibleIndex, lastVisibleIndex, scrollToIndex }) => {
    const shouldControlScrollRef = useRef(true);
    const enableControlledScroll = useCallback(() => {
        shouldControlScrollRef.current = true;
    }, []);
    const disableControlledScroll = useCallback(() => {
        shouldControlScrollRef.current = false;
    }, []);

    useEffect(() => {
        const NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'PageUp', 'PageDown', 'Home', 'End'];
        /**
         * @param {KeyboardEvent} event
         */
        const onKeyDown = event => {
            if (NAVIGATION_KEYS.includes(event.key)) {
                enableControlledScroll();
            }
        };

        const onMouseMove = disableControlledScroll;
        const onWheel = disableControlledScroll;

        document.addEventListener('keydown', onKeyDown, { capture: true });
        document.addEventListener('mousemove', onMouseMove, { capture: true });
        document.addEventListener('wheel', onWheel, { capture: true });

        return () => {
            document.removeEventListener('keydown', onKeyDown, { capture: true });
            document.removeEventListener('mousemove', onMouseMove, { capture: true });
            document.removeEventListener('wheel', onWheel, { capture: true });
        };
    }, [disableControlledScroll, enableControlledScroll]);

    useEffect(() => {
        const shouldControlScroll = shouldControlScrollRef.current;
        if (!shouldControlScroll) {
            return;
        }

        if (focusedIndex === -1 || firstVisibleIndex === -1 || lastVisibleIndex === -1) {
            return;
        }

        const isFocusedOptionAboveVisible = focusedIndex < firstVisibleIndex;
        const isFocusedOptionBelowVisible = focusedIndex > lastVisibleIndex;
        const isFocusedOptionHidden = isFocusedOptionAboveVisible || isFocusedOptionBelowVisible;
        if (isFocusedOptionHidden) {
            scrollToIndex(focusedIndex, { align: 'start' });
        }
    }, [firstVisibleIndex, lastVisibleIndex, focusedIndex, scrollToIndex]);
};

/**
 * @param {import('react-select').MenuListProps<import('./usePlanPickerApi').PlanPickerItemWithStatus>} props
 */
function VirtualizedPlanPickerMenuList({ innerProps, innerRef, children, ...rest }) {
    const { focusedOption, options, selectProps } = rest;
    const parentRef = useRef();
    const overscan = 1;
    const { totalSize, virtualItems, scrollToIndex } = useVirtual({
        size: options.length,
        parentRef,
        overscan,
        estimateSize: useCallback(() => 40, [])
    });
    const { loadOptionsByIndex, selectRef } = usePlanPickerContext();

    const startIndex = virtualItems.at(0)?.index ?? -1;
    const endIndex = virtualItems.at(-1)?.index ?? -1;
    const focusedIndex = options.indexOf(focusedOption);

    useFixReactSelectFocus(selectRef, options);
    useScrollManager({
        focusedIndex,
        firstVisibleIndex: startIndex > -1 ? startIndex + overscan : -1,
        lastVisibleIndex: endIndex > -1 ? endIndex - overscan : -1,
        scrollToIndex
    });

    useEffect(() => {
        if (startIndex === -1 || endIndex === -1) {
            return;
        }

        const optionsInView = options.slice(startIndex, endIndex + 1);
        /** @type {number[]} */
        const unloadedIndexes = optionsInView.reduce((indexes, option, i) => {
            if (option.status === 'unloaded') {
                // Remember to add startIndex back in to get the absolute index
                indexes.push(i + startIndex);
            }

            return indexes;
        }, []);

        if (unloadedIndexes.length) {
            const controller = new AbortController();
            loadOptionsByIndex(unloadedIndexes, controller.signal);

            // FIXME: Returning a cleanup function here is the "correct"
            // approach, according to React -- if the plan picker unmounts,
            // we want to abort the above async operation. But doing this
            // so far has actually broken lazy loading; the async load is
            // being aborted prematurely every time.
            // eslint-disable-next-line consistent-return
            // return () => {
            //     controller.abort();
            // };
        }
    }, [endIndex, loadOptionsByIndex, options, startIndex]);

    /** @type {import('react').ReactNode} */
    let menuUI;
    if (selectProps.isLoading || options.length === 0) {
        // Fallback to the default LoadingMessage or NoOptionsMessage
        menuUI = children;
    } else {
        const childrenAsArray = React.Children.toArray(children);
        menuUI = (
            <div
                style={{
                    height: `${totalSize}px`,
                    width: '100%',
                    position: 'relative'
                }}
            >
                {virtualItems.map(virtualItem => {
                    const child = childrenAsArray[virtualItem.index];

                    return (
                        <div
                            key={virtualItem.key}
                            className="dca-plan-picker__menu-row"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start}px)`
                            }}
                        >
                            {child}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <components.MenuList
            {...rest}
            innerProps={{
                ...innerProps,
                style: {
                    ...innerProps?.style,
                    width: '100%',
                    overflow: 'auto',
                    maxHeight: '300px'
                }
            }}
            innerRef={el => {
                parentRef.current = el;
                innerRef(el);
            }}
        >
            {menuUI}
        </components.MenuList>
    );
}

export default VirtualizedPlanPickerMenuList;

/**
 * @typedef {object} UseScrollManagerProps
 * @prop {number} focusedIndex
 * @prop {number} firstVisibleIndex
 * @prop {number} lastVisibleIndex
 * @prop {(index: number) => void} scrollToIndex
 */
